import { PersistedSession } from '@jupiterone/login-shared';

export type SsoAccount = {
  accountId: string;
  /** Present only when several accounts match and the page must show a chooser. */
  accountName?: string;
};

export type LookupResult = {
  ssoAccounts: SsoAccount[];
  /** The email can also sign in with Google or a password; present only with SSO accounts. */
  hasDirectSignIn?: boolean;
};

const jsonHeaders = { 'Content-Type': 'application/json' };

export function useLoginApi() {
  return {
    logout: async (): Promise<void> => {
      const response = await fetch('/auth/logout');
      if (response.status < 200 || response.status > 300) {
        throw new Error('Logout failed.');
      }
    },

    setSession: async (
      body: PersistedSession,
    ): Promise<{ redirect?: string }> => {
      const response = await fetch('/auth/session', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(body),
      });

      if (response.status >= 400)
        throw new Error('Failure to get authorization code');

      return response.json();
    },

    /** Which SSO account(s) an email can sign in to. */
    lookup: async (email: string): Promise<LookupResult> => {
      const response = await fetch('/auth/lookup', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ email }),
      });

      if (response.status >= 400) {
        const message = await response
          .json()
          .then((body: { message?: string }) => body?.message)
          .catch(() => undefined);
        throw new Error(
          message ?? 'Unable to look up your sign-in options. Try again.',
        );
      }

      return response.json();
    },

    forgotSso: async (email: string) => {
      const response = await fetch('/auth/forgotSSO', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ email }),
      });

      if (response.status >= 400) throw new Error('Failure to get login links');
    },
  };
}
